import './_shareForm.scss';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    Stack,
    Switch,
    Typography,
    styled,
} from '@mui/material';
import {
    DashboardOwnerT,
    DashboardT,
    GroupSharingT,
    OrgSharingT,
    SharingPayloadT,
    UserSharingT,
} from 'business/definitions';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import Flex from 'ui/Flex/Flex';
import { OrganizationSharing } from 'components/SharedItemUI/OrganizationSharing';
import { PublicSharing } from 'components/SharedItemUI/PublicSharing';
import SharingModalDefaultFilters from 'components/SharedItemUI/SharingModalDefaultFilters';
import UsersSharing from 'components/SharedItemUI/UsersSharing';
import { useTranslation } from 'react-i18next';
import { DECISION } from 'business/constants';
import { useGetSuggestedUsers } from 'hooks/useFetchSuggestedUsers';

const StySharingSection = styled(Flex, {
    name: 'StySharingSection',
})(({ theme }) => ({
    padding: theme.spacing(1),
}));

type ShareFormProps = {
    closeModal: () => void;
    shareKey: number;
    orgSharing: OrgSharingT;
    owner?: DashboardOwnerT;
    publicUrl?: string;
    show: boolean;
    submit: (arg: {
        sharing: Partial<SharingPayloadT>;
        key: number | string;
    }) => void;
    title: string;
    translationKey: string;
    usersSharing: UserSharingT[];
    groupsSharing?: GroupSharingT[];
    showLeftActions?: boolean;
    dashboard?: DashboardT;
};

const ShareForm = ({
    show,
    shareKey,
    title,
    translationKey,
    owner = undefined,
    usersSharing,
    groupsSharing = undefined,
    publicUrl = undefined,
    orgSharing,
    submit,
    showLeftActions = true,
    closeModal,
    dashboard,
}: ShareFormProps): JSX.Element => {
    const { t } = useTranslation();

    const { data: users } = useGetSuggestedUsers(DECISION, true, true);
    const usersEmails = useMemo(
        () => users?.map((user) => user.email),
        [users],
    );
    const shouldHandleUserGroups = groupsSharing !== undefined;
    const [currentGroupsSharing, setCurrentGroupsSharing] = useState(
        groupsSharing ?? [],
    );

    const [userSharing, setUsersSharing] = useState(usersSharing);
    const [isPublic, setIsPublic] = useState(orgSharing.isPublic);
    const [canWrite, setCanWrite] = useState(orgSharing.isRestrictedEditAccess);
    const [canRead, setCanRead] = useState(orgSharing.isRestricted);
    const [shouldSendEmail, setShouldSendEmail] = useState(showLeftActions);
    const [shouldSendNotification, setShouldSendNotification] =
        useState(showLeftActions);

    const onShouldSendEmailSwitchChange = (
        event: ChangeEvent<HTMLInputElement>,
    ) => setShouldSendEmail(event.target.checked);

    const onShouldSendNotificationSwitchChange = (
        event: ChangeEvent<HTMLInputElement>,
    ) => setShouldSendNotification(event.target.checked);

    useEffect(() => {
        if (isPublic) {
            setCanRead(true);
        }
    }, [isPublic, setCanRead]);

    useEffect(() => {
        if (!canRead) {
            setIsPublic(false);
            setCanWrite(false);
        }
    }, [canRead, setCanRead, setCanWrite]);

    const publicSharingView = publicUrl ? (
        <PublicSharing
            isPublic={isPublic}
            setIsPublic={setIsPublic}
            publicUrl={publicUrl}
            translationKey={translationKey}
        />
    ) : (
        <> </>
    );

    const orgSharingView = (
        <OrganizationSharing
            canRead={canRead}
            setCanRead={setCanRead}
            canWrite={canWrite}
            setCanWrite={setCanWrite}
            translationKey={translationKey}
        />
    );

    const userSharingView = (
        <UsersSharing
            disabled={canWrite}
            allUsers={usersEmails ?? []}
            selectedUsers={userSharing}
            setSelectedUsers={setUsersSharing}
            shouldHandleUserGroups={shouldHandleUserGroups}
            selectedUserGroups={currentGroupsSharing ?? []}
            setSelectedUserGroups={setCurrentGroupsSharing}
            ownerMail={owner ? owner.email : undefined}
            translationKey={translationKey}
        />
    );

    const handleShareFormClick = () => {
        const sharingPayload = {
            orgSharing: {
                isPublic,
                isRestricted: canRead,
                isRestrictedEditAccess: canWrite,
            },
            usersSharing: userSharing,
            ...(shouldHandleUserGroups
                ? {
                      groupsSharing: currentGroupsSharing.map(
                          (groupSharing) => ({
                              id: groupSharing.groupId,
                              editAccess: groupSharing.editAccess,
                          }),
                      ),
                  }
                : {}),
            ...(showLeftActions
                ? { shouldSendEmail, shouldSendNotification }
                : {}),
        };
        submit({ key: shareKey, sharing: sharingPayload });
        closeModal();
    };

    const shouldDisplayDefaultFilterContainer = dashboard && dashboard.canEdit;

    return (
        <Dialog open={show} onClose={closeModal} className="shareform">
            <DialogTitle variant="h3" color="primary">{`${t(
                `sharingModal.${translationKey}.title`,
            )} ${title}`}</DialogTitle>
            <DialogContent>
                {shouldDisplayDefaultFilterContainer && (
                    <SharingModalDefaultFilters dashboard={dashboard} />
                )}
                <StySharingSection direction="column">
                    <Typography variant="h5" color="primary">
                        {t('sharingModal.userSharingTitle')}
                    </Typography>
                    <List>
                        {userSharingView}
                        <Divider />
                        {orgSharingView}
                        <Divider />
                        {publicSharingView}
                    </List>
                </StySharingSection>
            </DialogContent>
            <DialogActions
                sx={{
                    justifyContent: showLeftActions ? 'space-between' : 'right',
                }}
            >
                {showLeftActions && (
                    <Flex direction="column">
                        <Flex align="center">
                            <Switch
                                checked={shouldSendEmail}
                                onChange={onShouldSendEmailSwitchChange}
                            />
                            <Typography variant="body2" display="inline">
                                {t('shareForm.shouldSendEmail')}
                            </Typography>
                        </Flex>
                        <Flex align="center">
                            <Switch
                                checked={shouldSendNotification}
                                onChange={onShouldSendNotificationSwitchChange}
                            />
                            <Typography variant="body2" display="inline">
                                {t('shareForm.shouldSendNotification')}
                            </Typography>
                        </Flex>
                    </Flex>
                )}
                <Stack direction="row" spacing="0.5rem">
                    <Button onClick={closeModal} color="primary">
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleShareFormClick}
                        color="primary"
                        variant="contained"
                        className="e2e-share-form-submit"
                    >
                        {t('common.confirm')}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default ShareForm;
